/**
 * PendingReviewsNudge — v2.5.7
 *
 * Surfaces invoices, W-9s, and COIs awaiting owner approval directly on
 * the Command Center dashboard. Easy to miss inside Project Hub → Reviews,
 * so we promote it to the top of the dashboard with a clear count + CTA.
 *
 * The nudge is dismissed automatically when nothing is pending. No persistent
 * "dismiss" state — pending items naturally clear as the owner reviews them.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, ArrowRight, FileText, ShieldCheck, FileCheck2 } from 'lucide-react';
import { apiService } from '@/lib/api';

export default function PendingReviewsNudge({ userId }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState({
    pending_invoices: [],
    pending_w9s: [],
    pending_cois: [],
    total_pending: 0,
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    apiService
      .getPendingReviews(userId)
      .then((res) => {
        if (!alive) return;
        setPending(res?.data || { pending_invoices: [], pending_w9s: [], pending_cois: [], total_pending: 0 });
      })
      .catch(() => { /* non-blocking */ })
      .finally(() => alive && setLoaded(true));
    return () => { alive = false; };
  }, [userId]);

  if (!loaded || pending.total_pending === 0) return null;

  const invoiceCount = pending.pending_invoices?.length || 0;
  const w9Count = pending.pending_w9s?.length || 0;
  const coiCount = pending.pending_cois?.length || 0;
  const topInvoice = (pending.pending_invoices || [])[0];

  return (
    <div
      className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4 mb-4 flex items-start gap-3 sm:items-center sm:flex-row flex-col"
      data-testid="pending-reviews-nudge"
    >
      <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
        <ClipboardCheck className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-900">
          {pending.total_pending} item{pending.total_pending === 1 ? '' : 's'} awaiting your review
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600 mt-0.5">
          {invoiceCount > 0 && (
            <span className="inline-flex items-center gap-1" data-testid="pending-reviews-invoices-count">
              <FileText className="w-3 h-3 text-emerald-600" /> {invoiceCount} invoice{invoiceCount === 1 ? '' : 's'}
            </span>
          )}
          {w9Count > 0 && (
            <span className="inline-flex items-center gap-1" data-testid="pending-reviews-w9s-count">
              <FileCheck2 className="w-3 h-3 text-amber-600" /> {w9Count} W-9{w9Count === 1 ? '' : 's'}
            </span>
          )}
          {coiCount > 0 && (
            <span className="inline-flex items-center gap-1" data-testid="pending-reviews-cois-count">
              <ShieldCheck className="w-3 h-3 text-blue-600" /> {coiCount} COI{coiCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {topInvoice && (
          <p className="text-[11px] text-slate-500 mt-1 truncate">
            Latest: <span className="font-semibold text-slate-700">${(topInvoice.amount || 0).toLocaleString()}</span> from {topInvoice.contractor_name} on {topInvoice.project_name}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => navigate('/project-hub?tab=reviews')}
        className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3.5 py-2 rounded-lg flex-shrink-0 self-stretch sm:self-auto"
        data-testid="pending-reviews-cta-btn"
      >
        Review now <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
